import { useCallback, useContext } from "react";
import {
  QueryClient,
  QueryClientContext,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import {
  getSessionCheckin,
  putSessionCheckin,
  getSessionFeedback,
  putSessionFeedback,
} from "@/__generated__/api/me/me";
import type {
  CheckinIn,
  CheckinStateOut,
  FeedbackIn,
  FeedbackStateOut,
} from "@/__generated__/api/wrenAPI.schemas";
import type { QueryShape } from "../meHooks";
import { ApiError } from "@/api/client";
import { apiErrorDetail } from "../expectedOutcome";

// ─────────────────────────────────────────────────────────────────────────────
// reflectionHooks (WP-3, contracts §4.4) — per-partner check-in + feedback.
//
// Wraps the generated /me reflection client and adapts the snake_case wire
// (`CheckinStateOut`/`FeedbackStateOut`) ⇄ camelCase view types, meHooks idiom.
// Query keys are the LOCKED §4.4 map — WP-2's hub/past-page invalidation
// coherence depends on these exact keys:
//   ["me","sessions", sessionUuid, "checkin"] / [..., "feedback"]
// and useSaveFeedback additionally invalidates ["me","sessions","past"]
// (`hasFeedback` flips → the hub prompt and Past-page CTA disappear live).
// ─────────────────────────────────────────────────────────────────────────────

// DERIVED, not restated. This union was declared here AND in `MoodQuadrantGrid`,
// which is two definition sites for one persisted vocabulary: a quadrant could
// exist in the grid with no member here, or here with no tile to pick it, and
// nothing failed. `CheckinIn.mood_quadrant` stores this key, so the grid that
// offers it owns it. See `flows/checkin/checkinVocabulary.ts`.
import type { MoodQuadrant } from "@codel/dova-ds";

export type { MoodQuadrant };

export interface CheckinDraft {
  moodQuadrant: MoodQuadrant;
  feelingWord: string;
  onYourMind: string | null;
  showedPartnerMatters: string | null;
  partnerShowedIMatter: string | null;
}

export interface CheckinState {
  windowOpen: boolean;
  checkin: (CheckinDraft & { submittedAt: string }) | null;
}

export interface FeedbackDraft {
  rating: number;
  helpful: string | null;
  didntLand: string | null;
  doDifferently: string | null;
}

export interface FeedbackState {
  windowOpen: boolean;
  feedback: (FeedbackDraft & { submittedAt: string }) | null;
}

/** The 409 `detail` for a second save: answers are final once saved. */
export const ALREADY_SUBMITTED = "reflection_already_submitted";

/**
 * True when a save failed because this partner already saved (a stale tab, a double tap).
 * Not a failure to the person: their answers ARE saved, so the flow shows the saved screen.
 * Read by CODE, because the same route's other 409 (window closed) is a sentence.
 */
export function isAlreadySubmitted(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409 && apiErrorDetail(error) === ALREADY_SUBMITTED;
}

export const checkinQueryKey = (sessionUuid: string) =>
  ["me", "sessions", sessionUuid, "checkin"] as const;
export const feedbackQueryKey = (sessionUuid: string) =>
  ["me", "sessions", sessionUuid, "feedback"] as const;
const PAST_SESSIONS_KEY = ["me", "sessions", "past"] as const;

const detachedQueryClient = new QueryClient();

function useQueryClientSafe(): QueryClient {
  return useContext(QueryClientContext) ?? detachedQueryClient;
}

// ── wire ⇄ view adapters ────────────────────────────────────────────────────

function checkinStateFromWire(wire: CheckinStateOut): CheckinState {
  return {
    windowOpen: wire.window_open,
    checkin: wire.checkin
      ? {
          moodQuadrant: wire.checkin.mood_quadrant as MoodQuadrant,
          feelingWord: wire.checkin.feeling_word,
          onYourMind: wire.checkin.on_your_mind ?? null,
          showedPartnerMatters: wire.checkin.showed_partner_matters ?? null,
          partnerShowedIMatter: wire.checkin.partner_showed_i_matter ?? null,
          submittedAt: wire.checkin.submitted_at,
        }
      : null,
  };
}

function checkinDraftToWire(draft: CheckinDraft): CheckinIn {
  return {
    mood_quadrant: draft.moodQuadrant,
    feeling_word: draft.feelingWord,
    on_your_mind: draft.onYourMind,
    showed_partner_matters: draft.showedPartnerMatters,
    partner_showed_i_matter: draft.partnerShowedIMatter,
  };
}

function feedbackStateFromWire(wire: FeedbackStateOut): FeedbackState {
  return {
    windowOpen: wire.window_open,
    feedback: wire.feedback
      ? {
          rating: wire.feedback.rating,
          helpful: wire.feedback.helpful ?? null,
          didntLand: wire.feedback.didnt_land ?? null,
          doDifferently: wire.feedback.do_differently ?? null,
          submittedAt: wire.feedback.submitted_at,
        }
      : null,
  };
}

function feedbackDraftToWire(draft: FeedbackDraft): FeedbackIn {
  return {
    rating: draft.rating,
    helpful: draft.helpful,
    didnt_land: draft.didntLand,
    do_differently: draft.doDifferently,
  };
}

// ── hooks ───────────────────────────────────────────────────────────────────

/** The ACTING partner's check-in state for a session (null until submitted). */
export function useCheckinState(
  sessionUuid: string,
  opts: { enabled?: boolean } = {}
): QueryShape<CheckinState> {
  const { enabled = true } = opts;
  const q = useQuery({
    queryKey: checkinQueryKey(sessionUuid),
    enabled,
    queryFn: async () => {
      const res = await getSessionCheckin(sessionUuid);
      return checkinStateFromWire(res.data as CheckinStateOut);
    },
  });
  return {
    data: q.data,
    isLoading: q.isLoading,
    error: q.error instanceof Error ? q.error : null,
  };
}

/**
 * Forces a refetch of a session's check-in state (e.g. window_open) without
 * waiting for a natural cache miss — the hub's ticking clock calls this at
 * session start so the check-in card drops live, matching the server's
 * save-until-start rule instead of trusting a stale cached boolean.
 */
export function useInvalidateCheckin(): (sessionUuid: string) => void {
  const queryClient = useQueryClientSafe();
  return useCallback(
    (sessionUuid: string) => {
      void queryClient.invalidateQueries({ queryKey: checkinQueryKey(sessionUuid) });
    },
    [queryClient]
  );
}

/** Save the acting partner's check-in, once. A second save is a 409 `reflection_already_submitted`. */
export function useSaveCheckin(
  sessionUuid: string
): UseMutationResult<CheckinState, Error, CheckinDraft> {
  const queryClient = useQueryClient();
  return useMutation<CheckinState, Error, CheckinDraft>({
    mutationFn: async (draft) => {
      const res = await putSessionCheckin(sessionUuid, checkinDraftToWire(draft));
      return checkinStateFromWire(res.data as CheckinStateOut);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: checkinQueryKey(sessionUuid) });
    },
  });
}

/** The ACTING partner's feedback state for a session (null until submitted). */
export function useFeedbackState(
  sessionUuid: string,
  opts: { enabled?: boolean } = {}
): QueryShape<FeedbackState> {
  const { enabled = true } = opts;
  const q = useQuery({
    queryKey: feedbackQueryKey(sessionUuid),
    enabled,
    queryFn: async () => {
      const res = await getSessionFeedback(sessionUuid);
      return feedbackStateFromWire(res.data as FeedbackStateOut);
    },
  });
  return {
    data: q.data,
    isLoading: q.isLoading,
    error: q.error instanceof Error ? q.error : null,
  };
}

/** Save the acting partner's feedback, once (no close window in v1). */
export function useSaveFeedback(
  sessionUuid: string
): UseMutationResult<FeedbackState, Error, FeedbackDraft> {
  const queryClient = useQueryClient();
  return useMutation<FeedbackState, Error, FeedbackDraft>({
    mutationFn: async (draft) => {
      const res = await putSessionFeedback(sessionUuid, feedbackDraftToWire(draft));
      return feedbackStateFromWire(res.data as FeedbackStateOut);
    },
    onSuccess: () => {
      // hasFeedback flips on the past list — the hub prompt and the Past-page
      // "Share feedback" CTA must disappear without a reload (FE-FBK-10).
      void queryClient.invalidateQueries({ queryKey: feedbackQueryKey(sessionUuid) });
      void queryClient.invalidateQueries({ queryKey: [...PAST_SESSIONS_KEY] });
    },
  });
}
